import * as Sentry from '@sentry/react';
import { clearErrorField, validationAndUpdateValue } from 'apps/auth/components/form-multi/utils';
import { fetchData } from 'apps/auth/helpers/api';
import { logInfo, logWarn, logError } from 'apps/auth/helpers/clientLogger';

const GOOGLE_MAP_PREDICTIONS = `/google-maps/predictions`;
const GOOGLE_MAPS_DETAILS = `/google-maps/details`;
const POSTAL_CODE = 'postal_code';

export class AutoAddressCompleteAddressService {
    #countryCode = '';
    #lang = '';
    #controllerRef = '';
    #formName = '';

    //fields
    #fieldCity;
    #fieldPostCode;
    #addressMainField;

    // functions
    #setItems;
    #selectItem;
    #changeFormFieldValue;
    #changeFormField;

    constructor(formName, lang, setItems, selectItem, controllerRef) {
        this.#formName = formName;
        this.#lang = lang;
        this.#setItems = setItems;
        this.#selectItem = selectItem;
        this.#controllerRef = controllerRef;
    }

    setCountryCode(countryCode) {
        this.#countryCode = countryCode;
    }

    setFields(fieldCity, fieldPostCode, addressMainField) {
        this.#fieldCity = fieldCity;
        this.#fieldPostCode = fieldPostCode;
        this.#addressMainField = addressMainField;
    }

    setReduxAction(changeFormFieldValue, changeFormField) {
        this.#changeFormFieldValue = changeFormFieldValue;
        this.#changeFormField = changeFormField;
    }

    setActions(setItems, selectItem) {
        this.#setItems = setItems;
        this.#selectItem = selectItem;
    }

    suggestionApiSearch(searchRef, setItems) {
        const setPredictions = (predictions) => {
            if (predictions !== null && !!predictions?.length) {
                setItems(predictions);
                this.#setItems(predictions);
            } else {
                setItems([]);
                this.#setItems([]);
            }
        };

        const searchWithApi = (term) => {
            if (term.length > 3) {
                this.#fetchPredictions(term).then((list) => {
                    setPredictions(list);
                });
            } else if (term.length === 0) {
                setItems([]);
                this.#setItems([]);
            }
        };

        const selectItem = (e, item) => {
            const { mainText } = this.serializeItem(item);
            searchRef.current.value = mainText.trim();
            this.#selectItem(e, item);
        };

        return [selectItem, searchWithApi];
    }

    selectPlace(setSelectedAddress, closeMenu, onAutoFill) {
        const setPlace = (place) => {
            const addressComponents = place?.addressComponents;
            if (addressComponents) {
                if (this.#fieldPostCode) {
                    setPostCode(addressComponents);
                }
                if (this.#fieldCity) {
                    setCityName(addressComponents);
                }
            }
        };

        const setCityName = (addressComponents = []) => {
            const locality = addressComponents.filter((a) => Array.isArray(a?.types) && a.types.includes('locality'))[0];
            const subLocality = addressComponents.filter(
                (a) =>
                    Array.isArray(a?.types) &&
                    (a.types.includes('locality') ||
                        a.types.includes('sublocality') ||
                        a.types.includes('sublocality_level_1') ||
                        a.types.includes('administrative_area_level_1') ||
                        a.types.includes('administrative_area_level_2')),
            )[0];
            let cityName = subLocality?.short_name ?? locality?.short_name;
            if (checkDigits(cityName)) {
                if (locality?.short_name && !checkDigits(locality.short_name)) {
                    cityName = locality.short_name;
                } else {
                    cityName = cityName.replace(/\d/g, '');
                }
            }
            const normalizedCityName = cityName?.trim();
            this.#changeFormFieldValue(this.#formName, normalizedCityName, this.#fieldCity.name);
            onAutoFill?.({ city: normalizedCityName });
        };

        const checkDigits = (str = '') => {
            for (let i = 0; i < str.length; i++) {
                if (str[i] >= '0' && str[i] <= '9') {
                    return true;
                }
            }
            return false;
        };

        const setPostCode = (addressComponents = []) => {
            const postalElm = addressComponents.find((elm) => elm.types[0].toLowerCase() === POSTAL_CODE);
            if (!postalElm) {
                this.#changeFormFieldValue(this.#formName, '', this.#fieldPostCode.name);
            }
            addressComponents.forEach((element) => {
                setPostCodeValue(element?.long_name, element?.types?.[0]);
            });
        };

        const setPostCodeValue = (postCode, type) => {
            if (postCode && type.toLowerCase() === POSTAL_CODE) {
                this.#changeFormFieldValue(this.#formName, postCode, this.#fieldPostCode.name);
                onAutoFill?.({ postCode });
            } else {
                if (this.#fieldPostCode.isError) {
                    clearErrorField(this.#formName, this.#fieldPostCode, this.#changeFormField).catch((e) => logWarn(e));
                }
                if (this.#fieldPostCode.value) {
                    this.#changeFormFieldValue(this.#formName, '', this.#fieldPostCode.name);
                }
            }
        };

        const setPlaceDetails = (prediction) => {
            try {
                const url = `${GOOGLE_MAPS_DETAILS}?id=${prediction?.placeId}&country=${this.#countryCode}&requestLang=${this.#lang}`;
                fetchData(url, 'POST', this.#controllerRef).then((place) => setPlace(place));
            } catch (e) {
                Sentry.captureException(e, {
                    level: 'warning',
                });
                logError(e);
            }
        };

        const selectItem = (e, prediction) => {
            setPlaceDetails(prediction);

            const value = this.serializeItem(prediction)?.mainText?.trim();
            validationAndUpdateValue(this.#formName, value, this.#addressMainField, this.#changeFormField).then(() => {
                closeMenu();
                setSelectedAddress(value, prediction.placeId);
            });
        };

        return [selectItem];
    }

    serializeItem = (item) => ({
        id: item['placeId'],
        mainText: item['structuredFormatting']['mainText'],
        secondaryText: item['structuredFormatting']['secondaryText'],
    });

    #fetchPredictions(term) {
        const url = `${GOOGLE_MAP_PREDICTIONS}?requestLang=${this.#lang}&country=${this.#countryCode}&query=${term}`;
        try {
            return fetchData(url, 'POST', this.#controllerRef);
        } catch (e) {
            Sentry.captureException(e, {
                level: 'log',
            });
            logInfo(e);
        }
    }
}
